import { useCallback, useMemo } from 'react';
import { Box } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { useSearchParams } from 'react-router-dom';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import type { MultipleChoiceFilterOption } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilterTypes';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export function useCourseFilter(): string[] {
  const [searchParams] = useSearchParams();
  return useMemo(() => {
    const raw = searchParams.get('courseIds') ?? '';
    return raw.split(',').filter((value) => value !== '');
  }, [searchParams]);
}

export function CourseFilter(): React.JSX.Element {
  const [, setSearchParams] = useSearchParams();
  const courseIds = useCourseFilter();
  const { data: courses } = trpcAdmin.workPlacements.placements.listCourseOptions.useQuery(
    undefined,
    { initialData: [] },
  );

  const options = useMemo(
    () => courses.map((course) => ({ id: course.id, value: course.id, label: course.title })),
    [courses],
  );

  const handleChange = useCallback(
    (filters: MultipleChoiceFilterOption) => {
      const nextCourseIds = filters.courseIds ?? [];
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextCourseIds.length === 0) {
            next.delete('courseIds');
          } else {
            next.set('courseIds', nextCourseIds.join(','));
          }
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <MultipleChoiceFilter
      items={options}
      label={t`Course`}
      name="courseIds"
      onFilterChange={handleChange}
      onlyUpdateOnChange
      value={courseIds.join(',')}
      variant="ghost"
      leftIcon={<Box size={iconSize.md} />}
      rightIcon={undefined}
    />
  );
}
